import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DOMPurify from 'dompurify';
import brandGuideService from '../../services/brandGuideService';
import { IBrandGuideSection } from '../../types/damTypes';

// Fontana brand colors
const COLORS = {
  olympia: '#1B3967',
  originalet: '#0050E6',
  sand: '#EDE8DC',
  sandLight: '#F5F0E8',
  sandDark: '#D4CFC3',
  white: '#FFFFFF',
  text: '#333333',
  textLight: '#666666',
};

interface BrandGuideSectionPageProps {
  slug: string;
  onBack: () => void;
  onNavigate?: (slug: string) => void;
  canEdit?: boolean;
  onEdit?: (slug: string) => void;
}

const BrandGuideSectionPage: React.FC<BrandGuideSectionPageProps> = ({
  slug,
  onBack,
  onNavigate,
  canEdit = false,
  onEdit,
}) => {
  const [section, setSection] = useState<IBrandGuideSection | null>(null);
  const [allSections, setAllSections] = useState<IBrandGuideSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hämta alla sektioner för snabbnavigering
  useEffect(() => {
    brandGuideService
      .fetchSections(true) // Endast publicerade
      .then((data) => setAllSections(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    brandGuideService
      .fetchSection(slug)
      .then((data) => {
        if (mounted) {
          setSection(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.response?.data?.message || err.message || 'Kunde inte hämta sektionen.');
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  // Sortera och filtrera sektioner för navigering
  const otherSections = useMemo(() => 
    allSections
      .filter(s => s.slug !== slug && s.isPublished)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [allSections, slug]
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          bgcolor: COLORS.sandLight,
        }}
      >
        <CircularProgress sx={{ color: COLORS.olympia }} />
      </Box>
    );
  }

  if (error || !section) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Sektionen hittades inte.'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ color: COLORS.olympia, borderColor: COLORS.olympia }}
        >
          Tillbaka till översikt
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100%', bgcolor: COLORS.white }}>
      {/* Top navigation bar */}
      <Box
        sx={{
          bgcolor: COLORS.white,
          borderBottom: `1px solid ${COLORS.sandDark}`,
          py: 1.5,
          px: { xs: 2, md: 4 },
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Översta raden: Tillbaka och Redigera */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: otherSections.length > 0 ? 1.5 : 0,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              color: COLORS.olympia,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: COLORS.sandLight },
            }}
          >
            Tillbaka till översikt
          </Button>
          {canEdit && onEdit && (
            <Button
              startIcon={<EditIcon />}
              variant="contained"
              onClick={() => onEdit(section.slug)}
              sx={{
                bgcolor: COLORS.olympia,
                color: COLORS.white,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: COLORS.originalet },
              }}
            >
              Redigera
            </Button>
          )}
        </Box>

        {/* Snabblänkar till andra sektioner */}
        {otherSections.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              pt: 1,
              borderTop: `1px solid ${COLORS.sand}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: COLORS.textLight,
                alignSelf: 'center',
                mr: 0.5,
                fontWeight: 500,
              }}
            >
              Hoppa till:
            </Typography>
            {otherSections.map((s) => (
              <Chip
                key={s._id}
                label={s.title}
                size="small"
                onClick={() => onNavigate ? onNavigate(s.slug) : null}
                sx={{
                  bgcolor: COLORS.sandLight,
                  color: COLORS.olympia,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: COLORS.sand,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Main content - PDF-style layout */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        {/* Left sidebar - Title section with sand background */}
        <Box
          sx={{
            width: { xs: '100%', lg: 400 },
            flexShrink: 0,
            bgcolor: COLORS.sandLight,
            p: { xs: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            // Sticky på desktop
            position: { xs: 'relative', lg: 'sticky' },
            top: { lg: 0 },
            alignSelf: { lg: 'flex-start' },
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Kapra Neue", "Oswald", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: COLORS.olympia,
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              lineHeight: 1.1,
              mb: 3,
            }}
          >
            {section.title}
          </Typography>

          {/* Introtext - renderas som HTML om den finns */}
          {section.introText ? (
            <Box
              sx={{
                fontSize: '1rem',
                lineHeight: 1.7,
                color: COLORS.text,
                mb: 3,
                '& h1': {
                  fontFamily: '"Kapra Neue", "Oswald", sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: COLORS.olympia,
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  mt: 2,
                  mb: 1,
                  '&:first-of-type': { mt: 0 },
                },
                '& h2': {
                  fontFamily: '"Kapra Neue", "Oswald", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: COLORS.olympia,
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  mt: 2,
                  mb: 1,
                  '&:first-of-type': { mt: 0 },
                },
                '& h3': {
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: COLORS.olympia,
                  mt: 1.5,
                  mb: 0.75,
                  '&:first-of-type': { mt: 0 },
                },
                '& p': { mb: 1.5 },
                '& strong': { fontWeight: 600 },
                '& ul, & ol': { pl: 2.5, mb: 1.5 },
                '& li': { mb: 0.5 },
                '& a': { color: COLORS.originalet, textDecoration: 'underline' },
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.introText || '') }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: '1rem',
                lineHeight: 1.7,
                color: COLORS.text,
                mb: 3,
              }}
            >
              {section.previewText}
            </Typography>
          )}

          {/* Status indicator */}
          <Box sx={{ mt: 'auto', pt: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 1,
                bgcolor: section.isPublished
                  ? 'rgba(46, 125, 50, 0.1)'
                  : 'rgba(237, 108, 2, 0.1)',
                color: section.isPublished ? '#2e7d32' : '#ed6c02',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'currentColor',
                }}
              />
              {section.isPublished ? 'Publicerad' : 'Utkast'}
            </Box>
          </Box>
        </Box>

        {/* Right content area */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, md: 5, lg: 6 },
            maxWidth: { lg: 900 },
          }}
        >
          {/* Rich HTML content */}
          <Box
            sx={{
              '& h1': {
                fontFamily: '"Kapra Neue", "Oswald", sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                color: COLORS.olympia,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                mt: 4,
                mb: 2,
                '&:first-of-type': { mt: 0 },
              },
              '& h2': {
                fontFamily: '"Kapra Neue", "Oswald", sans-serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: COLORS.olympia,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                mt: 4,
                mb: 2,
                '&:first-of-type': { mt: 0 },
              },
              '& h3': {
                fontWeight: 600,
                fontSize: '1.2rem',
                color: COLORS.olympia,
                mt: 3,
                mb: 1.5,
              },
              '& p': {
                fontSize: '1rem',
                lineHeight: 1.8,
                color: COLORS.text,
                mb: 2,
              },
              '& ul, & ol': {
                pl: 3,
                mb: 2,
              },
              '& li': {
                fontSize: '1rem',
                lineHeight: 1.8,
                color: COLORS.text,
                mb: 1,
                '&::marker': {
                  color: COLORS.originalet,
                },
              },
              '& strong': {
                fontWeight: 600,
                color: COLORS.olympia,
              },
              '& a': {
                color: COLORS.originalet,
                textDecoration: 'none',
                borderBottom: `1px solid ${COLORS.originalet}`,
                '&:hover': {
                  opacity: 0.8,
                },
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 2,
                my: 3,
              },
              '& img.with-shadow': {
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
              '& blockquote': {
                borderLeft: `4px solid ${COLORS.originalet}`,
                pl: 3,
                py: 1,
                my: 3,
                bgcolor: COLORS.sandLight,
                borderRadius: '0 8px 8px 0',
                fontStyle: 'italic',
                color: COLORS.textLight,
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                my: 3,
                '& th, & td': {
                  border: `1px solid ${COLORS.sandDark}`,
                  p: 2,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: COLORS.sandLight,
                  fontWeight: 600,
                  color: COLORS.olympia,
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.contentHtml || '') }}
          />

        </Box>
      </Box>
    </Box>
  );
};

export default BrandGuideSectionPage;
